import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../../layouts/Sidebar';
import Topbar from '../../layouts/Topbar';
import api from '../../config/api';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const fmt = (val, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(val);

const statusLabel = {
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    cancelled: 'Cancelled',
};

const statusBadgeClass = {
    draft: 'badge-soft-secondary',
    sent: 'badge-soft-primary',
    paid: 'badge-soft-success',
    cancelled: 'badge-soft-danger',
};

const statusColor = {
    draft: '#6c757d',
    sent: '#0d6efd',
    paid: '#198754',
    cancelled: '#dc3545',
};

/* ------------------------------------------------------------------ */
/*  Reports Component                                                  */
/* ------------------------------------------------------------------ */
const Reports = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [dashStats, setDashStats] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [statsRes, invRes, cliRes] = await Promise.all([
                api.get('/dashboard-stats/'),
                api.get('/invoices/'),
                api.get('/clients/'),
            ]);
            setDashStats(statsRes.data);
            setInvoices(invRes.data);
            setClients(cliRes.data);
        } catch (err) {
            console.error('Reports fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    /* ---- Filtered invoices ---- */
    const filtered = invoices.filter((inv) => {
        if (filterStatus && inv.status !== filterStatus) return false;
        if (filterClient && inv.client !== parseInt(filterClient)) return false;
        if (dateFrom && inv.invoice_date < dateFrom) return false;
        if (dateTo && inv.invoice_date > dateTo) return false;
        return true;
    });

    const filteredTotal = filtered.reduce((s, i) => s + parseFloat(i.grand_total || 0), 0);
    const filteredPaid = filtered
        .filter((i) => i.status === 'paid')
        .reduce((s, i) => s + parseFloat(i.grand_total || 0), 0);
    const filteredOutstanding = filtered
        .filter((i) => i.status === 'sent')
        .reduce((s, i) => s + parseFloat(i.grand_total || 0), 0);

    /* ---- Export to CSV ---- */
    const exportCSV = () => {
        const headers = [
            'Invoice #',
            'Client',
            'Status',
            'Date',
            'Due Date',
            'Currency',
            'Grand Total',
        ];
        const rows = filtered.map((inv) => [
            inv.invoice_number,
            inv.client_detail?.display_name || inv.client_name || `Client #${inv.client}`,
            inv.status,
            inv.invoice_date,
            inv.due_date,
            inv.currency,
            inv.grand_total,
        ]);
        const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoices_report_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    /* ---- Print Report ---- */
    const printReport = () => {
        const printWindow = window.open('', '_blank');
        const tableRows = filtered
            .map(
                (inv) => `
            <tr>
                <td>${inv.invoice_number}</td>
                <td>${inv.client_detail?.display_name || inv.client_name || 'N/A'}</td>
                <td><span style="padding:2px 8px;border-radius:4px;background:${statusColor[inv.status]}20;color:${statusColor[inv.status]}">${statusLabel[inv.status]}</span></td>
                <td>${inv.invoice_date}</td>
                <td>${inv.due_date}</td>
                <td style="text-align:right">${inv.currency} ${parseFloat(inv.grand_total).toFixed(2)}</td>
            </tr>`
            )
            .join('');

        printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Invoice Report</title>
<style>
    * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Segoe UI', sans-serif; }
    body { padding: 40px; color: #333; }
    h1 { font-size: 24px; margin-bottom: 4px; }
    .subtitle { color: #666; margin-bottom: 24px; }
    .summary { display: flex; gap: 24px; margin-bottom: 32px; }
    .summary-card { padding: 16px 24px; border: 1px solid #e0e0e0; border-radius: 8px; min-width: 160px; }
    .summary-card h3 { font-size: 14px; color: #888; margin-bottom: 4px; }
    .summary-card p { font-size: 20px; font-weight: 700; margin: 0; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f8f9fa; text-align: left; padding: 10px 12px; border-bottom: 2px solid #dee2e6; font-size: 13px; }
    td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
    @media print { body { padding: 20px; } }
</style>
</head><body>
    <h1>Invoice Report</h1>
    <p class="subtitle">Generated on ${new Date().toLocaleDateString()}</p>
    <div class="summary">
        <div class="summary-card"><h3>Total Amount</h3><p>${fmt(filteredTotal)}</p></div>
        <div class="summary-card"><h3>Paid</h3><p>${fmt(filteredPaid)}</p></div>
        <div class="summary-card"><h3>Outstanding</h3><p>${fmt(filteredOutstanding)}</p></div>
        <div class="summary-card"><h3>Invoices</h3><p>${filtered.length}</p></div>
    </div>
    <table>
        <thead><tr><th>Invoice #</th><th>Client</th><th>Status</th><th>Date</th><th>Due Date</th><th style="text-align:right">Amount</th></tr></thead>
        <tbody>${tableRows}</tbody>
    </table>
</body></html>`);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 300);
    };

    /* ---- Status distribution for inline chart ---- */
    const statusDist = {};
    filtered.forEach((inv) => {
        statusDist[inv.status] = (statusDist[inv.status] || 0) + 1;
    });

    const barStyle = (pct, color) => ({
        height: '100%',
        width: `${pct}%`,
        backgroundColor: color,
        transition: 'width 0.6s ease',
        minWidth: pct > 0 ? '4px' : '0',
    });

    /* ================================================================ */
    /*  RENDER                                                          */
    /* ================================================================ */
    return (
        <div
            data-sidebar-skin="dark"
            data-header-skin="light"
            data-navbar-brand-skin="dark"
            data-sidebar-state="expand"
        >
            <Topbar />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content pb-0">
                    {/* Header */}
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Reports</h4>
                            <p className="text-muted mb-0 fs-13">
                                Analyze your invoicing &amp; revenue data
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-outline-white border" onClick={fetchAll}>
                                <i className="ti ti-refresh me-1" />
                                Refresh
                            </button>
                            <button className="btn btn-sm btn-outline-success" onClick={exportCSV}>
                                <i className="ti ti-file-spreadsheet me-1" />
                                Export CSV
                            </button>
                            <button className="btn btn-sm btn-primary" onClick={printReport}>
                                <i className="ti ti-printer me-1" />
                                Print Report
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-tabs mb-4">
                        {['overview', 'invoices', 'clients'].map((tab) => (
                            <li className="nav-item" key={tab}>
                                <button
                                    className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        border: 'none',
                                        borderBottom: activeTab === tab ? '2px solid #7c5cfc' : '2px solid transparent',
                                        background: 'transparent',
                                        fontWeight: activeTab === tab ? 600 : 400,
                                        color: activeTab === tab ? '#7c5cfc' : '#6c757d',
                                        cursor: 'pointer',
                                        padding: '8px 20px',
                                    }}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {loading ? (
                        <div className="d-flex justify-content-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading…</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
                            {activeTab === 'overview' && dashStats && (
                                <>
                                    {/* KPI row */}
                                    <div className="row">
                                        <div className="col-md-6 col-xl-3 d-flex">
                                            <div className="card flex-fill border-start border-4 border-primary">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Total Revenue</p>
                                                    <h4 className="fw-bold mb-0">{fmt(dashStats.total_revenue)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-xl-3 d-flex">
                                            <div className="card flex-fill border-start border-4 border-success">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">This Month</p>
                                                    <h4 className="fw-bold mb-0">{fmt(dashStats.month_revenue)}</h4>
                                                    <span
                                                        className={`badge mt-1 ${
                                                            dashStats.revenue_change >= 0
                                                                ? 'badge-soft-success'
                                                                : 'badge-soft-danger'
                                                        }`}
                                                    >
                                                        {dashStats.revenue_change >= 0 ? '+' : ''}
                                                        {dashStats.revenue_change}% vs last month
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-xl-3 d-flex">
                                            <div className="card flex-fill border-start border-4 border-warning">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Outstanding</p>
                                                    <h4 className="fw-bold mb-0">{fmt(dashStats.outstanding)}</h4>
                                                    <span className="badge badge-soft-warning mt-1">
                                                        {dashStats.sent_count} invoices
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-xl-3 d-flex">
                                            <div className="card flex-fill border-start border-4 border-danger">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Overdue</p>
                                                    <h4 className="fw-bold mb-0">{dashStats.overdue_count}</h4>
                                                    <span className="badge badge-soft-danger mt-1">
                                                        Needs attention
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Revenue chart + Status breakdown */}
                                    <div className="row">
                                        <div className="col-lg-8 d-flex">
                                            <div className="card flex-fill">
                                                <div className="card-header">
                                                    <h6 className="fw-bold mb-0">Monthly Revenue (Last 6 Months)</h6>
                                                </div>
                                                <div className="card-body">
                                                    {dashStats.monthly_data.map((d, i) => {
                                                        const max = Math.max(
                                                            ...dashStats.monthly_data.map((m) => m.revenue),
                                                            1
                                                        );
                                                        return (
                                                            <div key={i} className="mb-3">
                                                                <div className="d-flex justify-content-between mb-1">
                                                                    <span className="fs-13">{d.month}</span>
                                                                    <span className="fs-13 fw-semibold">
                                                                        {fmt(d.revenue)}
                                                                    </span>
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '24px',
                                                                        backgroundColor: '#f0f0f5',
                                                                        borderRadius: '4px',
                                                                        overflow: 'hidden',
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            height: '100%',
                                                                            width: `${(d.revenue / max) * 100}%`,
                                                                            backgroundColor: '#7c5cfc',
                                                                            borderRadius: '4px',
                                                                            transition: 'width 0.8s ease',
                                                                            minWidth: d.revenue > 0 ? '6px' : '0',
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-4 d-flex">
                                            <div className="card flex-fill">
                                                <div className="card-header">
                                                    <h6 className="fw-bold mb-0">Invoice Status Breakdown</h6>
                                                </div>
                                                <div className="card-body">
                                                    {Object.entries({
                                                        paid: dashStats.paid_count,
                                                        sent: dashStats.sent_count,
                                                        draft: dashStats.draft_count,
                                                        cancelled: dashStats.cancelled_count,
                                                    }).map(([key, val]) => (
                                                        <div key={key} className="mb-3">
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span className="fs-13 text-capitalize d-flex align-items-center">
                                                                    <i
                                                                        className="ti ti-circle-filled me-1"
                                                                        style={{
                                                                            color: statusColor[key],
                                                                            fontSize: '10px',
                                                                        }}
                                                                    />
                                                                    {statusLabel[key]}
                                                                </span>
                                                                <span className="fs-13 fw-semibold">{val}</span>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    width: '100%',
                                                                    height: '8px',
                                                                    backgroundColor: '#f0f0f5',
                                                                    borderRadius: '4px',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <div
                                                                    style={barStyle(
                                                                        dashStats.total_invoices > 0
                                                                            ? (val / dashStats.total_invoices) * 100
                                                                            : 0,
                                                                        statusColor[key]
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <hr />
                                                    <div className="d-flex justify-content-between">
                                                        <span className="fw-semibold">Total</span>
                                                        <span className="fw-bold">{dashStats.total_invoices}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Top Clients */}
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header d-flex align-items-center justify-content-between">
                                                    <h6 className="fw-bold mb-0">Top 5 Clients by Revenue</h6>
                                                </div>
                                                <div className="card-body">
                                                    {dashStats.top_clients.length === 0 ? (
                                                        <p className="text-muted text-center py-3">No client data available.</p>
                                                    ) : (
                                                        <div className="table-responsive">
                                                            <table className="table table-hover">
                                                                <thead>
                                                                    <tr>
                                                                        <th>#</th>
                                                                        <th>Client</th>
                                                                        <th>Invoices</th>
                                                                        <th>Total Billed</th>
                                                                        <th>Share</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {dashStats.top_clients.map((c, i) => {
                                                                        const share =
                                                                            dashStats.total_revenue > 0
                                                                                ? (
                                                                                      (c.total_invoiced /
                                                                                          dashStats.total_revenue) *
                                                                                      100
                                                                                  ).toFixed(1)
                                                                                : 0;
                                                                        return (
                                                                            <tr key={c.id}>
                                                                                <td>{i + 1}</td>
                                                                                <td>
                                                                                    <div className="d-flex align-items-center">
                                                                                        <span
                                                                                            className={`avatar avatar-sm avatar-rounded ${
                                                                                                [
                                                                                                    'bg-teal',
                                                                                                    'bg-warning',
                                                                                                    'bg-orange',
                                                                                                    'bg-success',
                                                                                                    'bg-info',
                                                                                                ][i]
                                                                                            } text-white d-flex align-items-center justify-content-center`}
                                                                                        >
                                                                                            {c.initials}
                                                                                        </span>
                                                                                        <span className="ms-2 fw-semibold">
                                                                                            {c.name}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <span className="badge badge-md badge-soft-teal">
                                                                                        {c.invoice_count}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="fw-semibold">
                                                                                    {fmt(c.total_invoiced)}
                                                                                </td>
                                                                                <td>
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <div
                                                                                            style={{
                                                                                                width: '80px',
                                                                                                height: '6px',
                                                                                                backgroundColor: '#f0f0f5',
                                                                                                borderRadius: '3px',
                                                                                                overflow: 'hidden',
                                                                                            }}
                                                                                        >
                                                                                            <div
                                                                                                style={{
                                                                                                    height: '100%',
                                                                                                    width: `${share}%`,
                                                                                                    backgroundColor:
                                                                                                        '#7c5cfc',
                                                                                                    borderRadius: '3px',
                                                                                                    transition:
                                                                                                        'width 0.6s ease',
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                        <span className="fs-13">
                                                                                            {share}%
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ═══════════════ INVOICES TAB ═══════════════ */}
                            {activeTab === 'invoices' && (
                                <>
                                    {/* Filters */}
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="row g-3 align-items-end">
                                                <div className="col-md-3">
                                                    <label className="form-label fs-13">Status</label>
                                                    <select
                                                        className="form-select"
                                                        value={filterStatus}
                                                        onChange={(e) => setFilterStatus(e.target.value)}
                                                    >
                                                        <option value="">All Statuses</option>
                                                        <option value="draft">Draft</option>
                                                        <option value="sent">Sent</option>
                                                        <option value="paid">Paid</option>
                                                        <option value="cancelled">Cancelled</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fs-13">Client</label>
                                                    <select
                                                        className="form-select"
                                                        value={filterClient}
                                                        onChange={(e) => setFilterClient(e.target.value)}
                                                    >
                                                        <option value="">All Clients</option>
                                                        {clients.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                                {c.display_name || `${c.first_name} ${c.last_name}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label fs-13">From</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={dateFrom}
                                                        onChange={(e) => setDateFrom(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label fs-13">To</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={dateTo}
                                                        onChange={(e) => setDateTo(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <button
                                                        className="btn btn-outline-secondary w-100"
                                                        onClick={() => {
                                                            setFilterStatus('');
                                                            setFilterClient('');
                                                            setDateFrom('');
                                                            setDateTo('');
                                                        }}
                                                    >
                                                        <i className="ti ti-x me-1" />
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Summary cards */}
                                    <div className="row mb-4">
                                        <div className="col-md-4 d-flex">
                                            <div className="card flex-fill border-start border-4 border-primary">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Total (Filtered)</p>
                                                    <h4 className="fw-bold mb-0">{fmt(filteredTotal)}</h4>
                                                    <small className="text-muted">{filtered.length} invoices</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 d-flex">
                                            <div className="card flex-fill border-start border-4 border-success">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Paid (Filtered)</p>
                                                    <h4 className="fw-bold mb-0">{fmt(filteredPaid)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 d-flex">
                                            <div className="card flex-fill border-start border-4 border-warning">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Outstanding (Filtered)</p>
                                                    <h4 className="fw-bold mb-0">{fmt(filteredOutstanding)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status distribution bar */}
                                    {filtered.length > 0 && (
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <h6 className="fw-bold mb-3">Status Distribution</h6>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        height: '32px',
                                                        borderRadius: '6px',
                                                        overflow: 'hidden',
                                                        gap: '2px',
                                                    }}
                                                >
                                                    {Object.entries(statusDist).map(([st, cnt]) => (
                                                        <div
                                                            key={st}
                                                            title={`${statusLabel[st]}: ${cnt}`}
                                                            style={{
                                                                flex: cnt,
                                                                backgroundColor: statusColor[st],
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#fff',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                transition: 'flex 0.5s ease',
                                                            }}
                                                        >
                                                            {cnt > 0 && (
                                                                <span>
                                                                    {statusLabel[st]} ({cnt})
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Invoice table */}
                                    <div className="card">
                                        <div className="card-header d-flex align-items-center justify-content-between">
                                            <h6 className="fw-bold mb-0">
                                                Invoices ({filtered.length})
                                            </h6>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={exportCSV}
                                                >
                                                    <i className="ti ti-download me-1" />
                                                    CSV
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={printReport}
                                                >
                                                    <i className="ti ti-printer me-1" />
                                                    Print
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-hover mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Invoice #</th>
                                                            <th>Client</th>
                                                            <th>Status</th>
                                                            <th>Date</th>
                                                            <th>Due Date</th>
                                                            <th className="text-end">Amount</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filtered.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="7" className="text-center py-4 text-muted">
                                                                    No invoices match your filters.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            filtered.map((inv) => (
                                                                <tr key={inv.id}>
                                                                    <td className="fw-semibold">
                                                                        {inv.invoice_number}
                                                                    </td>
                                                                    <td>
                                                                        {inv.client_detail?.display_name ||
                                                                            inv.client_name ||
                                                                            `Client #${inv.client}`}
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className={`badge ${
                                                                                statusBadgeClass[inv.status]
                                                                            }`}
                                                                        >
                                                                            {statusLabel[inv.status]}
                                                                        </span>
                                                                    </td>
                                                                    <td>{inv.invoice_date}</td>
                                                                    <td>{inv.due_date}</td>
                                                                    <td className="text-end fw-semibold">
                                                                        {fmt(
                                                                            parseFloat(inv.grand_total),
                                                                            inv.currency
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <Link
                                                                            to={`/invoice-details/${inv.id}`}
                                                                            className="btn btn-sm btn-outline-primary"
                                                                        >
                                                                            <i className="ti ti-eye" />
                                                                        </Link>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ═══════════════ CLIENTS TAB ═══════════════ */}
                            {activeTab === 'clients' && (
                                <>
                                    <div className="row mb-4">
                                        <div className="col-md-4 d-flex">
                                            <div className="card flex-fill border-start border-4 border-primary">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">Total Clients</p>
                                                    <h4 className="fw-bold mb-0">{clients.length}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 d-flex">
                                            <div className="card flex-fill border-start border-4 border-success">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">With Active Invoices</p>
                                                    <h4 className="fw-bold mb-0">
                                                        {
                                                            new Set(
                                                                invoices
                                                                    .filter(
                                                                        (i) =>
                                                                            i.status === 'sent' ||
                                                                            i.status === 'draft'
                                                                    )
                                                                    .map((i) => i.client)
                                                            ).size
                                                        }
                                                    </h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 d-flex">
                                            <div className="card flex-fill border-start border-4 border-warning">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1 fs-13">With Paid Invoices</p>
                                                    <h4 className="fw-bold mb-0">
                                                        {
                                                            new Set(
                                                                invoices
                                                                    .filter((i) => i.status === 'paid')
                                                                    .map((i) => i.client)
                                                            ).size
                                                        }
                                                    </h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="card-header d-flex align-items-center justify-content-between">
                                            <h6 className="fw-bold mb-0">Client Summary</h6>
                                            <Link to="/create-client" className="btn btn-sm btn-primary">
                                                <i className="ti ti-plus me-1" />
                                                Add Client
                                            </Link>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-hover mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Client</th>
                                                            <th>Email</th>
                                                            <th>Invoices</th>
                                                            <th>Paid</th>
                                                            <th className="text-end">Total Billed</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {clients.length === 0 ? (
                                                            <tr>
                                                                <td
                                                                    colSpan="5"
                                                                    className="text-center py-4 text-muted"
                                                                >
                                                                    No clients yet.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            clients.map((c) => {
                                                                const cInvoices = invoices.filter(
                                                                    (i) => i.client === c.id
                                                                );
                                                                const cPaid = cInvoices.filter(
                                                                    (i) => i.status === 'paid'
                                                                );
                                                                const cTotal = cInvoices.reduce(
                                                                    (s, i) =>
                                                                        s + parseFloat(i.grand_total || 0),
                                                                    0
                                                                );
                                                                const displayName =
                                                                    c.display_name ||
                                                                    `${c.first_name} ${c.last_name}`.trim();
                                                                const initials = displayName
                                                                    .split(' ')
                                                                    .slice(0, 2)
                                                                    .map((w) => w[0]?.toUpperCase() || '')
                                                                    .join('');
                                                                return (
                                                                    <tr key={c.id}>
                                                                        <td>
                                                                            <div className="d-flex align-items-center">
                                                                                <span className="avatar avatar-sm avatar-rounded bg-teal text-white d-flex align-items-center justify-content-center me-2">
                                                                                    {initials}
                                                                                </span>
                                                                                <span className="fw-semibold">
                                                                                    {displayName}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-muted">
                                                                            {c.email || '—'}
                                                                        </td>
                                                                        <td>
                                                                            <span className="badge badge-md badge-soft-primary">
                                                                                {cInvoices.length}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="badge badge-md badge-soft-success">
                                                                                {cPaid.length}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end fw-semibold">
                                                                            {fmt(cTotal)}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
                {/* end content */}

                {/* Footer */}
                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Reports;
